import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { RELATION_COLORS, RELATION_LABELS } from './familyTreeData';

const {
  FiX, FiCheckCircle, FiClock, FiAlertCircle, FiShield,
  FiUserCheck, FiMail, FiMessageSquare, FiFilter, FiSearch,
  FiChevronDown, FiChevronUp, FiUser, FiCalendar,
} = FiIcons;

const STATUS_CONFIG = {
  verified:   { label: 'Verified',   color: '#059669', bg: '#ECFDF5', icon: FiCheckCircle },
  pending:    { label: 'Pending',    color: '#D97706', bg: '#FFFBEB', icon: FiClock },
  unverified: { label: 'Unverified', color: '#6B7280', bg: '#F3F4F6', icon: FiAlertCircle },
};

const FILTER_OPTIONS = [
  { id: 'all',        label: 'All Relationships' },
  { id: 'verified',   label: 'Verified' },
  { id: 'pending',    label: 'Pending Review' },
  { id: 'unverified', label: 'Unverified' },
];

const SORT_OPTIONS = [
  { id: 'status',    label: 'Status' },
  { id: 'name',      label: 'Name' },
  { id: 'type',      label: 'Relation Type' },
  { id: 'requested', label: 'Requested' },
];

const FamilyTreeVerificationModal = ({
  isOpen,
  onClose,
  nodes,
  links,
  verifications,
  onUpdateVerification,
}) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('status');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [requestNote, setRequestNote] = useState('');
  const [showRequestForm, setShowRequestForm] = useState(null);
  const [responseNote, setResponseNote] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(null);

  if (!isOpen) return null;

  const nodeMap = useMemo(() => {
    const map = {};
    nodes.forEach(n => { map[n.id] = n; });
    return map;
  }, [nodes]);

  const linkVerifications = useMemo(() => {
    const verifMap = {};
    verifications.forEach(v => {
      verifMap[v.linkId] = v;
    });

    return links.map(link => {
      const sourceNode = nodeMap[link.source];
      const targetNode = nodeMap[link.target];
      if (!sourceNode || !targetNode) return null;

      const linkId = `${link.source}-${link.target}-${link.type}`;
      const v = verifMap[linkId] || {
        linkId,
        sourceId: link.source,
        targetId: link.target,
        type: link.type,
        status: 'unverified',
        verifiedBy: null,
        verifiedAt: null,
        requestedBy: null,
        requestedAt: null,
        notes: null,
      };

      return {
        ...v,
        sourceName: sourceNode.name,
        targetName: targetNode.name,
        sourceAvatar: sourceNode.avatar,
        targetAvatar: targetNode.avatar,
      };
    }).filter(Boolean);
  }, [links, nodeMap, verifications]);

  const filtered = useMemo(() => {
    let items = [...linkVerifications];
    if (filter !== 'all') {
      items = items.filter(v => v.status === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(v =>
        v.sourceName.toLowerCase().includes(q) ||
        v.targetName.toLowerCase().includes(q) ||
        RELATION_LABELS[v.type]?.toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      if (sortBy === 'status') {
        const order = { verified: 0, pending: 1, unverified: 2 };
        return (order[a.status] ?? 2) - (order[b.status] ?? 2);
      }
      if (sortBy === 'name') {
        return a.sourceName.localeCompare(b.sourceName);
      }
      if (sortBy === 'type') {
        return (RELATION_LABELS[a.type] ?? '').localeCompare(RELATION_LABELS[b.type] ?? '');
      }
      if (sortBy === 'requested') {
        return (b.requestedAt ?? '').localeCompare(a.requestedAt ?? '');
      }
      return 0;
    });
    return items;
  }, [linkVerifications, filter, search, sortBy]);

  const counts = useMemo(() => ({
    total: linkVerifications.length,
    verified: linkVerifications.filter(v => v.status === 'verified').length,
    pending: linkVerifications.filter(v => v.status === 'pending').length,
    unverified: linkVerifications.filter(v => v.status === 'unverified').length,
  }), [linkVerifications]);

  const handleRequest = (item) => {
    onUpdateVerification(item.linkId, {
      status: 'pending',
      requestedBy: 'You',
      requestedAt: new Date().toISOString(),
      notes: requestNote.trim() || null,
    });
    setShowRequestForm(null);
    setRequestNote('');
  };

  const handleApprove = (item) => {
    onUpdateVerification(item.linkId, {
      status: 'verified',
      verifiedBy: 'You',
      verifiedAt: new Date().toISOString(),
      notes: responseNote.trim() || item.notes,
    });
    setShowResponseForm(null);
    setResponseNote('');
  };

  const handleReject = (item) => {
    onUpdateVerification(item.linkId, {
      status: 'unverified',
      verifiedBy: null,
      verifiedAt: null,
      notes: responseNote.trim() || null,
    });
    setShowResponseForm(null);
    setResponseNote('');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-3xl max-h-[85vh] bg-white rounded-[32px] shadow-2xl border border-brand-dark overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-brand-light">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-serif text-accent-navy">Family Tree Verification</h3>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-0.5">
                  {counts.verified} verified · {counts.pending} pending · {counts.unverified} unverified
                </p>
              </div>
            </div>
            <button onClick={onClose}
              className="w-10 h-10 rounded-xl border-2 border-brand-dark text-text-muted hover:text-accent-navy hover:border-accent-gold transition-all flex items-center justify-center">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-3 gap-px bg-brand-light">
            {[
              { key: 'verified',   label: 'Verified',   count: counts.verified,   color: 'text-green-600', bg: 'bg-green-50' },
              { key: 'pending',    label: 'Pending',    count: counts.pending,    color: 'text-amber-600', bg: 'bg-amber-50' },
              { key: 'unverified', label: 'Unverified', count: counts.unverified, color: 'text-gray-500',  bg: 'bg-gray-50' },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`p-3 text-center transition-all ${filter === s.key ? s.bg : 'bg-white hover:bg-brand-light'}`}
              >
                <div className={`text-2xl font-serif font-bold ${s.color}`}>{s.count}</div>
                <div className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-0.5">{s.label}</div>
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-brand-light space-y-3">
            <div className="flex gap-2">
              {FILTER_OPTIONS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                    filter === f.id
                      ? 'bg-accent-navy border-accent-navy text-white'
                      : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <div className="relative flex-grow">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-3.5 h-3.5" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or relation..."
                  className="w-full pl-9 pr-4 py-2 bg-white border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-xs font-medium transition-colors"
                />
              </div>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 bg-white border-2 border-brand-dark rounded-xl text-[9px] font-black uppercase tracking-widest text-text-muted focus:border-accent-gold outline-none cursor-pointer"
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted w-3 h-3 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiShield} className="w-10 h-10 text-brand-dark mx-auto mb-3" />
                <p className="text-sm text-text-muted font-medium">No relationships found</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              filtered.map(item => {
                const statusCfg = STATUS_CONFIG[item.status];
                const StatusIcon = statusCfg.icon;
                const isExpanded = expandedId === item.linkId;
                const relColor = RELATION_COLORS[item.type] ?? '#9CA3AF';
                const canRequest = item.status === 'unverified';
                const canRespond = item.status === 'pending' && item.requestedBy !== 'You';

                return (
                  <motion.div
                    key={item.linkId}
                    layout
                    className="border-2 rounded-2xl overflow-hidden transition-colors"
                    style={{
                      borderColor: isExpanded ? `${relColor}50` : undefined,
                      background: isExpanded ? `${relColor}08` : 'white',
                    }}
                  >
                    {/* Row */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : item.linkId)}
                      className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-brand-light/50 transition-colors"
                    >
                      {/* Source avatar */}
                      <img
                        src={item.sourceAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.sourceName)}&background=1E3A5F&color=C9A84C&bold=true&size=64`}
                        alt={item.sourceName}
                        className="w-10 h-10 rounded-xl object-cover border border-white shadow-sm shrink-0"
                      />
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-accent-navy truncate">{item.sourceName}</span>
                          <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md" style={{ background: `${relColor}20`, color: relColor }}>
                            {RELATION_LABELS[item.type] ?? item.type}
                          </span>
                          <span className="text-[8px] font-black uppercase tracking-widest text-text-muted">of</span>
                          <span className="text-xs font-bold text-accent-navy truncate">{item.targetName}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest"
                            style={{ background: statusCfg.bg, color: statusCfg.color }}>
                            <StatusIcon className="w-2.5 h-2.5" />
                            {statusCfg.label}
                          </span>
                          {item.verifiedBy && (
                            <span className="text-[8px] text-text-muted font-medium">
                              by {item.verifiedBy}
                            </span>
                          )}
                          {item.requestedBy && item.status === 'pending' && (
                            <span className="text-[8px] text-amber-600 font-medium">
                              requested by {item.requestedBy}
                            </span>
                          )}
                        </div>
                      </div>
                      <SafeIcon
                        icon={isExpanded ? FiChevronUp : FiChevronDown}
                        className="w-4 h-4 text-text-muted shrink-0"
                      />
                    </button>

                    {/* Expanded detail */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-3.5 pb-3.5 pt-1 space-y-3 border-t border-brand-light">
                            {/* Target avatar row */}
                            <div className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-brand-light">
                              <img
                                src={item.targetAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.targetName)}&background=1E3A5F&color=C9A84C&bold=true&size=64`}
                                alt={item.targetName}
                                className="w-9 h-9 rounded-lg object-cover border border-white shadow-sm shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-accent-navy truncate">{item.targetName}</p>
                                <p className="text-[8px] font-black uppercase tracking-widest text-text-muted">
                                  Connected as {RELATION_LABELS[item.type] ?? item.type}
                                </p>
                              </div>
                            </div>

                            {/* Notes */}
                            {item.notes && (
                              <div className="flex items-start gap-2 p-2.5 bg-brand-light/70 rounded-xl">
                                <SafeIcon icon={FiMessageSquare} className="w-3 h-3 text-text-muted shrink-0 mt-0.5" />
                                <p className="text-[10px] text-text-muted leading-relaxed">{item.notes}</p>
                              </div>
                            )}

                            {/* Timestamps */}
                            <div className="flex gap-4 text-[8px] text-text-muted font-medium">
                              {item.verifiedAt && (
                                <span className="flex items-center gap-1">
                                  <SafeIcon icon={FiCalendar} className="w-2.5 h-2.5" />
                                  Verified {new Date(item.verifiedAt).toLocaleDateString()}
                                </span>
                              )}
                              {item.requestedAt && (
                                <span className="flex items-center gap-1">
                                  <SafeIcon icon={FiClock} className="w-2.5 h-2.5" />
                                  Requested {new Date(item.requestedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>

                            {/* Actions */}
                            {canRequest && (
                              <div>
                                {showRequestForm === item.linkId ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={requestNote}
                                      onChange={e => setRequestNote(e.target.value)}
                                      placeholder="Add a note about why this relationship should be verified..."
                                      className="w-full p-3 bg-white border-2 border-brand-dark rounded-xl text-xs focus:border-accent-gold outline-none resize-none"
                                      rows={2}
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleRequest(item)}
                                        className="flex-1 py-2 bg-accent-navy text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all"
                                      >
                                        Submit Request
                                      </button>
                                      <button
                                        onClick={() => { setShowRequestForm(null); setRequestNote(''); }}
                                        className="px-4 py-2 border-2 border-brand-dark text-text-muted rounded-xl text-[9px] font-black uppercase tracking-widest hover:border-accent-gold transition-all"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setShowRequestForm(item.linkId)}
                                    className="w-full py-2.5 bg-amber-50 border-2 border-amber-200 text-amber-700 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-amber-100 transition-all flex items-center justify-center gap-2"
                                  >
                                    <SafeIcon icon={FiMail} className="w-3 h-3" />
                                    Request Verification
                                  </button>
                                )}
                              </div>
                            )}

                            {canRespond && (
                              <div>
                                {showResponseForm === item.linkId ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={responseNote}
                                      onChange={e => setResponseNote(e.target.value)}
                                      placeholder="Add a response note (optional)..."
                                      className="w-full p-3 bg-white border-2 border-brand-dark rounded-xl text-xs focus:border-accent-gold outline-none resize-none"
                                      rows={2}
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleApprove(item)}
                                        className="flex-1 py-2 bg-green-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                                      >
                                        <SafeIcon icon={FiCheckCircle} className="w-3 h-3" />
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => handleReject(item)}
                                        className="flex-1 py-2 bg-red-50 border-2 border-red-200 text-red-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-100 transition-all flex items-center justify-center gap-2"
                                      >
                                        <SafeIcon icon={FiX} className="w-3 h-3" />
                                        Reject
                                      </button>
                                      <button
                                        onClick={() => { setShowResponseForm(null); setResponseNote(''); }}
                                        className="px-4 py-2 border-2 border-brand-dark text-text-muted rounded-xl text-[9px] font-black uppercase tracking-widest hover:border-accent-gold transition-all"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => setShowResponseForm(item.linkId)}
                                      className="flex-1 py-2.5 bg-green-50 border-2 border-green-200 text-green-700 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-green-100 transition-all flex items-center justify-center gap-2"
                                    >
                                      <SafeIcon icon={FiUserCheck} className="w-3 h-3" />
                                      Review Request
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {item.status === 'verified' && (
                              <div className="flex items-center gap-2 p-2.5 bg-green-50 rounded-xl border border-green-200">
                                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-600 shrink-0" />
                                <span className="text-[10px] text-green-700 font-medium">
                                  This relationship has been verified{ item.verifiedBy ? ` by ${item.verifiedBy}` : '' }.
                                </span>
                              </div>
                            )}

                            {item.status === 'pending' && item.requestedBy === 'You' && (
                              <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-xl border border-amber-200">
                                <SafeIcon icon={FiClock} className="w-4 h-4 text-amber-600 shrink-0" />
                                <span className="text-[10px] text-amber-700 font-medium">
                                  Your verification request is awaiting review.
                                </span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-brand-light bg-brand-light/30">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                {filtered.length} of {linkVerifications.length} relationships shown
              </p>
              {counts.unverified > 0 && (
                <button
                  onClick={() => {
                    setFilter('unverified');
                    setSortBy('name');
                  }}
                  className="px-4 py-2 bg-accent-navy text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all"
                >
                  Review Unverified ({counts.unverified})
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FamilyTreeVerificationModal;